import type { SVGProps } from 'react';
import type { IconName } from '../icons/names.generated';
import { ICONS } from '../icons/registry.generated';

export type { IconName };

type Props = Omit<SVGProps<SVGSVGElement>, 'name'> & {
  /** Nombre del icono en Material Symbols. */
  name: IconName | undefined;
  /** Texto accesible. Sin él el icono es decorativo y se oculta al lector de
   *  pantalla, que es lo correcto cuando va junto a una etiqueta que ya lo dice. */
  label?: string;
  /** Tooltip nativo, como el `title` del `<span>` de antes. */
  title?: string;
  /** Variante rellena del mismo icono.
   *
   *  La fuente lo conseguía con `style={{ fontVariationSettings: "'FILL' 1" }}`,
   *  que es un eje variable y **no tiene efecto sobre un SVG plano**. Como ahí
   *  relleno y contorno significan cosas distintas —interruptor encendido,
   *  pieza impresa—, se resuelve con el fichero `-fill`, que es otro dibujo. */
  filled?: boolean;
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Icono SVG en línea. Sustituye a la fuente Material Symbols que las dos SPAs
 * cargaban con un <link> a Google, entregándole la IP de cada usuario.
 *
 * Se dimensiona en `1em`, así que **obedece a las clases de tamaño de texto**
 * existentes (`!text-[16px]`, `!text-[13px]`…) exactamente igual que hacía el
 * glifo de la fuente, y el relleno es `currentColor`, así que `text-primary` y
 * compañía siguen valiendo. Por eso el cambio no toca ni una clase.
 *
 * ⚠️ DIFERENCIA CON LA WEB: allí `IconName` es la union de los ~3.900 iconos de
 * la biblioteca y un `prebuild` regenera el registro en cada build. Aquí NO se
 * puede: los Dockerfile de tenant y admin hacen `npm ci` solo con el
 * package.json de la app y ANTES de copiar `shared/`, y `.dockerignore` excluye
 * todos los `node_modules`, así que la biblioteca de iconos no existe dentro
 * del build. Por eso `IconName` se genera desde el REGISTRO —solo los iconos ya
 * generados— y el registro se versiona. Sale ganando: usar un icono que no está
 * generado es un error de `tsc`, no un hueco en blanco que nadie ve. Si te pasa:
 *
 *     cd shared/ui && npm run gen:icons
 *
 * Admite `undefined` a propósito: es habitual pintarlo desde un mapa donde la
 * clave puede no existir. En ese caso no se pinta nada, igual que hacía el
 * `<span>` vacío.
 */
export function Icon({ name, className, label, title, filled, ...rest }: Props) {
  const path = name ? ICONS[filled ? `${name}-fill` : name] : undefined;

  if (!path) {
    if (name && import.meta.env?.DEV) {
      console.warn(`[Icon] "${name}" no está en el registro. Ejecuta: cd shared/ui && npm run gen:icons`);
    }
    return null;
  }

  return (
    <svg
      viewBox="0 -960 960 960"
      width="1em"
      height="1em"
      fill="currentColor"
      className={`bp-icon${className ? ` ${className}` : ''}`}
      role={label ? 'img' : undefined}
      aria-label={label}
      // Decorativo POR DEFECTO, que es lo correcto cuando el icono acompaña a
      // una etiqueta que ya lo dice. Pero si quien llama le pone su propio
      // `aria-label` o un `role` —un icono que ES el botón, como el interruptor
      // de impresora activa—, forzar `aria-hidden` lo borraría del lector de
      // pantalla y dejaría el control sin nombre.
      aria-hidden={label || rest['aria-label'] || rest.role ? undefined : true}
      {...rest}
      dangerouslySetInnerHTML={{
        __html: (title ? `<title>${escapeHtml(title)}</title>` : '') + path,
      }}
    />
  );
}
